import React, { useState, useEffect } from 'react';
import ExpiredListingNotice from '@/components/ExpiredListingNotice';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Navigation, Star, Calendar, Weight, MessageSquare, ArrowLeft,
  Ban, Copy, AlertTriangle, AlertCircle, Clock, Pencil, Send, CheckCircle2, Package, Trash2,
} from 'lucide-react';
import ProposeModal from '@/components/ProposeModal';
import { isNegotiable } from '@/utils/propositionPricing';
import ConfirmDialog from '@/components/ConfirmDialog';
import { tripPriceDisplay } from '@/utils/tripPriceUtils';
import { CONDITION_LABELS } from '@/utils/listingParsers';
import { cityFlag as flag } from '@/utils/cityFlag';
import { isEntreprise } from '@/utils/entreprise';
import { BACKEND_URL } from '@/config';
import VerificationBadges from '@/components/VerificationBadges';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const STATUS = {
  DRAFT:          { label: 'Brouillon',  tone: 'gray' },
  PENDING_REVIEW: { label: 'En attente', tone: 'blue' },
  PUBLISHED:      { label: 'Publié',     tone: 'teal' },
  ACTIVE:         { label: 'Disponible', tone: 'teal' },
  FULL:           { label: 'Complet',    tone: 'gold' },
  COMPLETED:      { label: 'Livré',      tone: 'teal' },
  EXPIRED:        { label: 'Expiré',     tone: 'gray' },
  REJECTED:       { label: 'Rejeté',     tone: 'red' },
  CANCELLED:      { label: 'Annulé',     tone: 'red' },
};

const TRANSPORT_LABELS = {
  avion: '✈️ Avion', voiture: '🚗 Voiture', bus: '🚌 Bus / Car',
  bateau: '⛴️ Bateau', train: '🚆 Train', autre: '📦 Autre',
};

const FLEX_LABELS = {
  pm3j: '± 3 jours',
  pm1s: '± 1 semaine',
  pm2s: '± 2 semaines',
};


function parseDesc(desc) {
  if (!desc) return {};
  const parts = desc.split(' · ');
  const out = { raw: [] };
  for (const p of parts) {
    if (p.startsWith('Transport:')) {
      const val = p.replace('Transport:', '').trim().toLowerCase();
      out.transport = Object.keys(TRANSPORT_LABELS).find(k => TRANSPORT_LABELS[k].toLowerCase().includes(val)) || null;
      out.transportLabel = p.replace('Transport:', '').trim();
    } else if (p.startsWith('Max ') && p.includes('kg/colis')) {
      out.maxPerPkg = p;
    } else if (p.startsWith('Dim. max:')) {
      out.dimensions = p.replace('Dim. max:', '').trim();
    } else if (p.includes('colis max')) {
      out.nbMax = p;
    } else if (p.startsWith('Catégories:')) {
      out.categories = p.replace('Catégories:', '').trim();
    } else if (p.startsWith('Tarif ')) {
      out.pricingMode = p;
    } else if (p.startsWith('Min ') || (p.startsWith('Max ') && !p.includes('kg/colis'))) {
      out.raw.push(p);
    } else if (p.startsWith('Devise:') || p.startsWith('Devises:')) {
      out.devise = p.replace(/^Devises?:\s*/, '').trim();
    } else if (p === 'Prix ferme') {
      out.firm = true;
    } else if (p.startsWith('Conditions:')) {
      out.conditions = p.replace('Conditions:', '').trim().split(',').map(s => s.trim()).filter(Boolean);
    } else if (p.startsWith('Message:')) {
      out.message = p.replace('Message:', '').trim();
    } else if (p.startsWith('Arrivée estimée:')) {
      out.arrival = p.replace('Arrivée estimée:', '').trim();
    } else if (p.startsWith('Flexibilité:')) {
      out.flex = p.replace('Flexibilité:', '').trim();
    } else {
      if (!out.note) out.note = p;
    }
  }
  return out;
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}
function formatDateTime(d) {
  if (!d) return '·';
  return new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TripDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeMode, setProposeMode] = useState('direct');
  const [confirmDlg, setConfirmDlg] = useState(null); // { action: 'cancel'|'delete'|'redirect', meta? }

  const load = () => {
    setLoading(true);
    axios.get(`${API}/trips/${id}`)
      .then(r => setTrip(r.data))
      .catch(() => toast.error('Trajet introuvable'))
      .finally(() => setLoading(false));
  };

  const refreshCapacity = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/trips/${id}`);
      setTrip(r.data);
    } catch { /* silent */ }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}><div className="skeleton" style={{ height: 320, borderRadius: 14 }} /></div>;
  if (!trip) return <div className="empty-state"><div className="empty-state-title">Trajet introuvable</div></div>;

  const isOwner = trip.user_id === user?.id;
  const status = STATUS[trip.status] || { label: trip.status, tone: 'gray' };
  const moderation = trip.moderation || {};
  const parsed = parseDesc(trip.description);

  // Champs directs avec fallback description (rétrocompatibilité anciens trajets)
  const transportModes = trip.transport_modes?.length > 0
    ? trip.transport_modes
    : (parsed.transport ? [parsed.transport] : []);

  const arrivalDateDisplay = trip.arrival_date
    ? formatDate(trip.arrival_date)
    : (parsed.arrival || null);

  const flexDisplay = trip.flex_departure && FLEX_LABELS[trip.flex_departure]
    ? FLEX_LABELS[trip.flex_departure]
    : (parsed.flex && parsed.flex !== 'Fixe' ? parsed.flex : null);

  const maxPerParcel = trip.max_weight_per_parcel != null
    ? trip.max_weight_per_parcel
    : (parsed.maxPerPkg ? parseFloat(parsed.maxPerPkg.replace('Max ', '').replace(' kg/colis', '')) : null);

  const maxParcels = trip.max_parcels != null
    ? trip.max_parcels
    : (parsed.nbMax ? parseInt(parsed.nbMax) : null);

  const categories = (() => {
    const raw = trip.accepted_categories?.length > 0
      ? trip.accepted_categories
      : (parsed.categories ? parsed.categories.split(',').map(s => s.trim()).filter(Boolean) : []);
    return raw.length > 1 ? raw.filter(c => c !== 'Autre') : raw;
  })();

  const conditions = trip.conditions?.length > 0
    ? trip.conditions
    : (parsed.conditions || []);

  const isPrixFerme = trip.counter_offers === false || parsed.firm;

  const message = trip.message_to_senders || parsed.message || null;

  const noteLibre = (() => {
    if (!parsed.note) return null;
    const autoNote = `Trajet ${trip.departure_city} → ${trip.arrival_city}`;
    return parsed.note === autoNote ? null : parsed.note;
  })();

  // Devises — champs directs en priorité
  const primaryCurrency = trip.primary_currency || null;
  const allCurrencies = trip.currencies?.length > 0 ? trip.currencies : null;
  const showPrimary = primaryCurrency && primaryCurrency !== 'EUR';
  const showAllCurrencies = allCurrencies && allCurrencies.length > 1;

  const handleCancel = () => {
    if (trip.blocking_cancel_mission_id) {
      setConfirmDlg({ action: 'redirect', meta: trip.blocking_cancel_mission_id });
      return;
    }
    setConfirmDlg({ action: 'cancel' });
  };

  const doCancel = async () => {
    setConfirmDlg(null);
    try {
      await axios.post(`${API}/trips/${id}/cancel`);
      toast.success('Trajet annulé.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = () => setConfirmDlg({ action: 'delete' });

  const doDelete = async () => {
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/trips/${id}`);
      toast.success('Trajet supprimé.');
      navigate('/app/trips');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  const handleRepublish = () => {
    sessionStorage.setItem('c2s_trip_duplicate', JSON.stringify({
      departure_city: trip.departure_city,
      arrival_city: trip.arrival_city,
      departure_country: trip.departure_country,
      arrival_country: trip.arrival_country,
      available_weight_kg: trip.available_weight_kg,
      price_per_kg: trip.price_per_kg,
      description: trip.description,
      transport_modes: trip.transport_modes,
      transport_custom: trip.transport_custom,
      flex_departure: trip.flex_departure,
      arrival_date: trip.arrival_date,
      max_weight_per_parcel: trip.max_weight_per_parcel,
      max_parcels: trip.max_parcels,
      accepted_categories: trip.accepted_categories,
      category_custom: trip.category_custom,
      pricing_mode: trip.pricing_mode,
      price_fixed: trip.price_fixed,
      currencies: trip.currencies,
      primary_currency: trip.primary_currency,
      counter_offers: trip.counter_offers,
      conditions: trip.conditions,
      message_to_senders: trip.message_to_senders,
    }));
    toast.success('Données copiées — adaptez si besoin avant de republier.');
    navigate('/app/trips/new?duplicate=1');
  };

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <button type="button" className="mlc-btn mlc-btn--ghost" style={{ marginBottom: 18, height: 32, fontSize: 12 }} onClick={() => navigate(-1)} data-testid="btn-back">
        <ArrowLeft size={14} /> Retour
      </button>

      {trip.status === 'REJECTED' && (
        <div className="detail-banner detail-banner--red" data-testid="banner-rejected">
          <div className="detail-banner-icon"><AlertCircle size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Trajet refusé par la modération</div>
            {moderation.reason && <div className="detail-banner-text"><strong>Motif&nbsp;:</strong> {moderation.reason}</div>}
            {moderation.rejected_at && <div className="detail-banner-meta">Refusé le {formatDateTime(moderation.rejected_at)}</div>}
            {isOwner && (
              <button type="button" className="mlc-btn mlc-btn--primary" style={{ height: 32, fontSize: 12, marginTop: 10 }} onClick={handleRepublish} data-testid="btn-republish">
                <Copy size={14} /> Modifier et republier
              </button>
            )}
          </div>
        </div>
      )}
      {trip.status === 'PENDING_REVIEW' && (
        <div className="detail-banner detail-banner--blue" data-testid="banner-pending">
          <div className="detail-banner-icon"><Clock size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">En attente de validation</div>
            <div className="detail-banner-text">Votre trajet sera vérifié par notre équipe avant publication.</div>
          </div>
        </div>
      )}
      {trip.status === 'CANCELLED' && (
        <div className="detail-banner detail-banner--gray" data-testid="banner-cancelled">
          <div className="detail-banner-icon"><Ban size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Trajet annulé</div>
            <div className="detail-banner-text">Ce trajet n'apparaît plus sur la marketplace.</div>
            {isOwner && (
              <button type="button" className="mlc-btn mlc-btn--ghost" style={{ height: 32, fontSize: 12, marginTop: 10 }} onClick={handleRepublish} data-testid="btn-duplicate">
                <Copy size={14} /> Dupliquer
              </button>
            )}
          </div>
        </div>
      )}
      {trip.status === 'EXPIRED' && (
        <ExpiredListingNotice kind="trajet" date={trip.departure_date} editPath={`/app/trips/${trip.id}/edit`}
          isOwner={isOwner} onDuplicate={handleRepublish} />
      )}
      {trip.status === 'COMPLETED' && (
        <div className="detail-banner detail-banner--teal" data-testid="banner-completed">
          <div className="detail-banner-icon"><CheckCircle2 size={20} /></div>
          <div className="detail-banner-body">
            <div className="detail-banner-title">Trajet terminé</div>
            <div className="detail-banner-text">Tous les colis ont été livrés avec succès.</div>
          </div>
        </div>
      )}

      <div className="detail-card">
        <div className="detail-head">
          <div className="detail-head-info">
            {/* Badges statut + PRO + modes de transport */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
              <span className={`mlc-badge mlc-badge--${status.tone}`} data-testid="status-pill">{status.label}</span>
              {isEntreprise(trip.owner) && <span className="mlc-badge mlc-badge--entreprise">Entreprise</span>}
              {trip.owner?.pro_subscribed && <span className="mlc-badge mlc-badge--gold" title="Transporteur abonné PRO">PRO</span>}
              {transportModes.map(t => (
                <span key={t} className="mlc-badge mlc-badge--gray">{TRANSPORT_LABELS[t] || t}</span>
              ))}
            </div>
            <h1 className="detail-title" data-testid="trip-title">{flag(trip.departure_city)} {trip.departure_city} → {flag(trip.arrival_city)} {trip.arrival_city}</h1>
            {/* Pays */}
            {(trip.departure_country || trip.arrival_country) && (
              <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2, marginBottom: 4 }}>
                {trip.departure_country || '·'} → {trip.arrival_country || '·'}
              </div>
            )}
            <div className="detail-route" style={{ color: 'var(--ink3)' }}>
              <Calendar size={13} /> {formatDate(trip.departure_date)}
            </div>
          </div>
          <div className="detail-price">
            {(() => {
              const p = tripPriceDisplay(trip);
              return (
                <>
                  <div className="detail-price-amount" style={trip.pricing_mode === 'libre' ? { fontSize: '1.3rem' } : {}}>
                    {p.amount}{p.unit && <span className="detail-price-currency">{p.unit}</span>}
                  </div>
                  <div className="detail-price-label">{p.label}</div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Grille d'infos */}
        <div className="detail-grid">
          <InfoBlock icon={<Calendar size={15} />} label="Départ" value={formatDate(trip.departure_date)} />
          <InfoBlock icon={<Weight size={15} />} label="Capacité disponible" value={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {trip.available_weight_kg} kg
              <button
                type="button"
                onClick={refreshCapacity}
                disabled={refreshing}
                style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 12, padding: 0, fontWeight: 600 }}
                title="La capacité évolue en temps réel — cliquez pour actualiser"
              >
                {refreshing ? '…' : '↺'}
              </button>
            </span>
          } />
          {arrivalDateDisplay && (
            <InfoBlock icon={<Calendar size={15} />} label="Arrivée estimée" value={arrivalDateDisplay} />
          )}
          {flexDisplay && (
            <InfoBlock icon={<Clock size={15} />} label="Flexibilité départ" value={flexDisplay} />
          )}
          {maxPerParcel != null && (
            <InfoBlock icon={<Package size={15} />} label="Max / colis" value={`${maxPerParcel} kg`} />
          )}
          {parsed.dimensions && (
            <InfoBlock icon={<Package size={15} />} label="Dimensions max" value={parsed.dimensions} />
          )}
          {maxParcels != null && (
            <InfoBlock icon={<Package size={15} />} label="Colis max" value={maxParcels} />
          )}
          {/* Devises acceptées — ★ = principale */}
          {showAllCurrencies && (
            <InfoBlock icon={<span style={{ fontSize: 14 }}>💰</span>} label="Devises acceptées"
              value={allCurrencies.map(c => c === primaryCurrency ? `★ ${c}` : c).join(', ')} />
          )}
          {/* Devise principale seule (pas de liste multi-devises) */}
          {!showAllCurrencies && showPrimary && (
            <InfoBlock icon={<span style={{ fontSize: 14 }}>💱</span>} label="Devise principale" value={`★ ${primaryCurrency}`} />
          )}
          {/* Fallback description pour anciens trajets */}
          {!primaryCurrency && parsed.devise && parsed.devise !== 'EUR' && (
            <InfoBlock icon={<span style={{ fontSize: 14 }}>💱</span>} label="Devise" value={parsed.devise} />
          )}
        </div>

        {/* Description libre du transporteur */}
        {noteLibre && (
          <div className="detail-section">
            <div className="detail-section-label">Description du trajet</div>
            <div className="detail-section-text">{noteLibre}</div>
          </div>
        )}

        {/* Message aux expéditeurs */}
        {message && (
          <div className="detail-section">
            <div className="detail-section-label">Message du transporteur</div>
            <div className="detail-section-text" style={{ fontStyle: 'italic', color: 'var(--ink2)' }}>« {message} »</div>
          </div>
        )}

        {/* Catégories acceptées */}
        {categories.length > 0 && (
          <div className="detail-section">
            <div className="detail-section-label">Catégories acceptées</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {categories.map(c => (
                <span key={c} className="mlc-badge mlc-badge--gray" style={{ fontSize: 12 }}>{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Conditions d'acceptation + prix ferme */}
        {(conditions.length > 0 || isPrixFerme) && (
          <div className="detail-section">
            <div className="detail-section-label">Conditions d'acceptation</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
              {conditions.map(c => (
                <span key={c} className="mlc-badge mlc-badge--teal" style={{ fontSize: 12 }}>
                  {CONDITION_LABELS[c] || c}
                </span>
              ))}
              {isPrixFerme && <span className="mlc-badge mlc-badge--gray" style={{ fontSize: 12 }}>🔒 Prix ferme</span>}
            </div>
          </div>
        )}

        {trip.owner && (
          <div className="detail-owner">
            <div className="av av-gold" style={{ width: 44, height: 44, fontSize: 14 }}>{(trip.owner.first_name || '?')[0]}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="detail-owner-name">{trip.owner.first_name} {trip.owner.last_name}</div>
              <div className="detail-owner-meta">
                {trip.owner.avg_rating && <span><Star size={11} fill="var(--gold)" color="var(--gold)" /> {trip.owner.avg_rating} ({trip.owner.rating_count || 0})</span>}
                <VerificationBadges user={trip.owner} />
              </div>
            </div>
            {!isOwner && (
              <button type="button" className="mlc-btn mlc-btn--ghost" style={{ height: 32, fontSize: 12 }} onClick={() => navigate('/app/messages')} data-testid="btn-contact-trip">
                <MessageSquare size={13} /> Contacter
              </button>
            )}
          </div>
        )}
      </div>

      {isOwner && trip.blocking_cancel_mission_id && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#fffbeb', border: '1px solid rgba(146,64,14,.18)', borderRadius: 10, padding: '10px 14px', margin: '0 0 12px' }}>
          <AlertCircle size={15} style={{ color: '#92400e', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: '#78350f', lineHeight: 1.45 }}>
            <strong>Annulation impossible</strong> — une mission est en cours sur ce trajet (paiement engagé ou colis en transit).{' '}
            <button type="button" onClick={() => navigate(`/app/missions/${trip.blocking_cancel_mission_id}`)}
              style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit' }}>
              Voir la mission
            </button>
          </div>
        </div>
      )}

      <div className="detail-actions">
        {isOwner && ['ACTIVE','PUBLISHED','PENDING_REVIEW'].includes(trip.status) && (
          <>
            {['ACTIVE','PUBLISHED'].includes(trip.status) && (
              <>
                <button data-testid="btn-trip-matching" className="mlc-btn mlc-btn--primary" onClick={() => navigate(`/app/trips/${trip.id}/matching`)}>
                  <Navigation size={14} /> Trouver des colis
                </button>
                <button data-testid="btn-trip-offers" className="mlc-btn mlc-btn--ghost" onClick={() => navigate(`/app/trips/${trip.id}/offers`)}>
                  <MessageSquare size={14} /> Offres reçues
                </button>
              </>
            )}
            <button className="mlc-btn mlc-btn--ghost" onClick={() => navigate(`/app/trips/${trip.id}/edit`)} data-testid="btn-edit">
              <Pencil size={14} /> Modifier
            </button>
            <button className="mlc-btn mlc-btn--ghost" onClick={handleCancel} data-testid="btn-cancel" style={{ color: '#B91C1C', borderColor: 'rgba(185,28,28,.25)' }}>
              <Ban size={14} /> Annuler
            </button>
            <button className="mlc-btn mlc-btn--ghost" onClick={handleDelete} data-testid="btn-delete-trip" style={{ color: '#7f1d1d', borderColor: 'rgba(127,29,29,.25)' }}>
              <Trash2 size={14} /> Supprimer
            </button>
          </>
        )}
        {isOwner && trip.status === 'FULL' && (
          <>
            <button className="mlc-btn mlc-btn--ghost" onClick={() => navigate('/app/messages')} data-testid="btn-contact">
              <MessageSquare size={14} /> Messagerie
            </button>
            <button className="mlc-btn mlc-btn--ghost" onClick={() => toast.info('Bientôt disponible')} data-testid="btn-report" style={{ color: '#B91C1C', borderColor: 'rgba(185,28,28,.25)' }}>
              <AlertTriangle size={14} /> Signaler un problème
            </button>
          </>
        )}
        {!isOwner && (user?.roles || []).includes('ROLE_SENDER') && ['ACTIVE','PUBLISHED'].includes(trip.status) && (() => {
          const isLibre = trip.pricing_mode === 'libre';
          const negotiable = isNegotiable('sender', trip);
          return (
            <>
              {!isLibre && (
                <button
                  data-testid="btn-propose-on-trip"
                  className="mlc-btn mlc-btn--primary"
                  onClick={() => { setProposeMode('direct'); setProposeOpen(true); }}
                >
                  <Send size={14} /> Faire une proposition
                </button>
              )}
              {negotiable ? (
                <button
                  data-testid="btn-negotiate-on-trip"
                  className="mlc-btn"
                  style={{ background: '#722f37', color: '#fff', borderColor: '#722f37' }}
                  onClick={() => { setProposeMode('negotiation'); setProposeOpen(true); }}
                >
                  💬 Négocier
                </button>
              ) : (
                <button
                  data-testid="btn-non-negotiable-trip"
                  className="mlc-btn mlc-btn--ghost"
                  disabled
                  style={{ color: '#9ca3af', cursor: 'not-allowed', borderColor: '#e5e7eb' }}
                >
                  🔒 Non Négociable
                </button>
              )}
            </>
          );
        })()}
        {!isOwner && (
          <button data-testid="btn-contact-trip-bar" className="mlc-btn mlc-btn--ghost" onClick={() => navigate('/app/messages')}>
            <MessageSquare size={14} /> Contacter
          </button>
        )}
      </div>

      <ProposeModal
        open={proposeOpen}
        onClose={() => { setProposeOpen(false); setProposeMode('direct'); }}
        meSide="sender"
        target={trip}
        mode={proposeMode}
        onSuccess={() => navigate('/app/propositions?tab=sent')}
      />

      {/* Annuler */}
      <ConfirmDialog
        open={confirmDlg?.action === 'cancel'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doCancel}
        variant="destructive"
        icon="ban"
        title="Annuler ce trajet ?"
        subtitle="Il sera conservé dans l'historique avec le statut Annulé."
        confirmLabel="Oui, annuler"
      />

      {/* Supprimer */}
      <ConfirmDialog
        open={confirmDlg?.action === 'delete'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={doDelete}
        variant="destructive"
        icon="trash"
        title="Supprimer ce trajet ?"
        warn="Les propositions en attente seront également supprimées."
        confirmLabel="Supprimer définitivement"
      />

      {/* Mission bloquante — redirection */}
      <ConfirmDialog
        open={confirmDlg?.action === 'redirect'}
        onClose={() => setConfirmDlg(null)}
        onConfirm={() => { setConfirmDlg(null); navigate(`/app/missions/${confirmDlg?.meta}`); }}
        variant="destructive"
        icon="ban"
        title="Annulation impossible"
        subtitle="Ce trajet a une mission en cours (paiement engagé ou colis en transit)."
        confirmLabel="Voir la mission concernée"
      />
    </div>
  );
}

function InfoBlock({ icon, label, value }) {
  return (
    <div className="detail-info-block">
      <div className="detail-info-icon">{icon}</div>
      <div>
        <div className="detail-info-label">{label}</div>
        <div className="detail-info-value">{value}</div>
      </div>
    </div>
  );
}
